import { Component, type ErrorInfo, type ReactNode } from 'react';
import * as Sentry from '@sentry/browser';
import { ClosedDoorCard } from '../shared/components/ClosedDoorCard.js';

interface BootErrorBoundaryProps {
  children: ReactNode;
}

interface BootErrorBoundaryState {
  failed: boolean;
}

/**
 * Boot-level error boundary wrapping RealApp. Without it, a synchronous
 * render-time throw leaves a blank page — most importantly the DiscordSDK
 * constructor, which throws a plain Error when the prod URL is opened
 * outside a Discord Activity iframe (no `frame_id` query param; see the
 * verification notes in realDiscordSdkAdapter.ts). There is no guest
 * fallback: the graceful answer is the closed-door surface telling the
 * player where Susdle actually runs.
 *
 * The reason is picked from the URL, not the error message (SDK message
 * text is not a stable API): a missing `frame_id` means we are not inside
 * an Activity iframe → `outsideDiscord`. Anything else that throws during
 * boot render gets the generic `couldntConnect` door, whose reload action
 * re-runs boot.
 */
export class BootErrorBoundary extends Component<BootErrorBoundaryProps, BootErrorBoundaryState> {
  override state: BootErrorBoundaryState = { failed: false };

  static getDerivedStateFromError(): BootErrorBoundaryState {
    return { failed: true };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    // The boundary swallows the throw, so Sentry's global handler never
    // sees it — report explicitly (no-op when Sentry is uninitialized).
    Sentry.captureException(error);
    console.error('[boot] render failed', error, info.componentStack);
  }

  override render(): ReactNode {
    if (this.state.failed) {
      return <ClosedDoorCard reason={isOutsideDiscordFrame() ? 'outsideDiscord' : 'couldntConnect'} />;
    }
    return this.props.children;
  }
}

/** True when the page URL lacks Discord's Activity `frame_id` param — i.e.
 *  the bundle was opened in a plain browser tab, not an Activity iframe. */
function isOutsideDiscordFrame(): boolean {
  return !new URL(window.location.href).searchParams.has('frame_id');
}
